<template>
  <div
    :class="
      cn('flex min-w-96 flex-col rounded-xl ', variant === 'hover' ? 'bg-white' : 'bg-glass60 ')
    "
  >
    <div
      v-for="row in detailRows"
      :key="row.title"
      :class="cn('flex gap-2 items-start text-s-medium px-2 py-1.5')"
    >
      <Icon
        :icon="row.icon"
        :class="
          cn(
            'size-5 shrink-0 ',
            variant === 'hover' ? ' text-(--color-b-trans50)' : 'text-(--color-b-trans50)',
          )
        "
      />
      <span
        :class="
          cn(
            'min-w-0 wrap-break-word leading-5 ',
            variant === 'hover' ? ' text-(--color-b-trans50)' : 'text-(--color-b-trans50)',
          )
        "
        >{{ row.title }}</span
      >
      <span
        v-for="(value, index) in row.value"
        :key="index"
        :class="
          cn(
            'min-w-0 wrap-break-word leading-5',
            variant === 'hover' ? ' text-(--color-b-trans95)' : ' text-black-white',
          )
        "
        >{{ value }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Icon, cn } from '@rapoo/element'
  import { RT_CONFIG_SECTION_META, type RtPresetConfig } from '../../types'
  import type { RtVibrationFeedbackType } from '@rapoo/device-sdk'

  const props = withDefaults(
    defineProps<{
      preset: RtPresetConfig
      variant?: 'hover' | 'dialog'
    }>(),
    {
      variant: 'hover',
    },
  )

  const variant = computed(() => props.variant)

  const { t } = useI18n()

  const formatLevel = (value: number) => (value === 0 ? t('rt.common.off') : String(value))
  const formatSound = (value: number) =>
    value === 0 ? t('rt.common.off') : t('rt.sound.option', { value })
  const formatVolume = (value: number) => {
    return value === 0 ? t('rt.common.off') : t('rt.volume.option', { value, unit: '%' })
  }

  const formatSideValues = (left: string, right: string, detail?: string) =>
    [
      detail,
      t('rt.common.leftValue', { value: left }),
      t('rt.common.rightValue', { value: right }),
    ].filter((value): value is string => Boolean(value))

  const vibrationTypeLabels = computed<Record<RtVibrationFeedbackType, string>>(() => ({
    0: t('rt.vibration.types.mechanical'),
    1: t('rt.vibration.types.optical'),
    2: t('rt.vibration.types.electromagnetic'),
  }))

  interface PresetDetailRow {
    title: string
    icon: string
    value: string[]
  }

  const detailRows = computed<PresetDetailRow[]>(() => {
    const { settings } = props.preset
    return [
      {
        title: t(RT_CONFIG_SECTION_META.travel.titleKey),
        icon: RT_CONFIG_SECTION_META.travel.icon,
        value: formatSideValues(
          formatLevel(settings.travel.triggerPress.left),
          formatLevel(settings.travel.triggerPress.right),
        ),
      },
      {
        title: t(RT_CONFIG_SECTION_META.rapidTrigger.titleKey),
        icon: RT_CONFIG_SECTION_META.rapidTrigger.icon,
        value: formatSideValues(
          settings.rapidTrigger.rtSwitch.left
            ? formatLevel(settings.rapidTrigger.rtLevel.left)
            : t('rt.common.off'),
          settings.rapidTrigger.rtSwitch.right
            ? formatLevel(settings.rapidTrigger.rtLevel.right)
            : t('rt.common.off'),
        ),
      },
      {
        title: t(RT_CONFIG_SECTION_META.vibration.titleKey),
        icon: RT_CONFIG_SECTION_META.vibration.icon,
        value: formatSideValues(
          formatLevel(settings.vibration.vibrationPress.left),
          formatLevel(settings.vibration.vibrationPress.right),
          vibrationTypeLabels.value[settings.vibration.feedbackType],
        ),
      },
      {
        title: t(RT_CONFIG_SECTION_META.sound.titleKey),
        icon: RT_CONFIG_SECTION_META.sound.icon,
        value: formatSideValues(
          `${formatSound(settings.sound.beepPress.left)}   ${formatVolume(settings.sound.beepVolume.left)}`,
          `${formatSound(settings.sound.beepPress.right)}   ${formatVolume(settings.sound.beepVolume.right)}`,
        ),
      },
    ]
  })
</script>
